import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LogOut, ExternalLink, Trash2, Phone, Clock, Package, MessageCircle, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  fetchOrders, updateOrderStatus, deleteOrder, getToken, logout,
} from "@/lib/adminApi";
import { formatPrice, BRAND } from "@/lib/i18n";

const STATUSES = [
  { key: "all", label: "Toutes" },
  { key: "new", label: "Nouvelles" },
  { key: "contacted", label: "Contactées" },
  { key: "confirmed", label: "Confirmées" },
  { key: "done", label: "Livrées" },
  { key: "cancelled", label: "Annulées" },
];

const STATUS_COLORS = {
  new: { bg: "rgba(251,191,36,0.18)", border: "#FBBF24", text: "#FCD34D" },
  contacted: { bg: "rgba(59,130,246,0.18)", border: "#3B82F6", text: "#93C5FD" },
  confirmed: { bg: "rgba(34,197,94,0.18)", border: "#22C55E", text: "#86EFAC" },
  done: { bg: "rgba(148,163,184,0.18)", border: "#94A3B8", text: "#CBD5E1" },
  cancelled: { bg: "rgba(239,68,68,0.18)", border: "#EF4444", text: "#FCA5A5" },
};

const STATUS_LABEL = {
  new: "Nouvelle", contacted: "Contactée", confirmed: "Confirmée",
  done: "Livrée", cancelled: "Annulée",
};

export default function AdminOrders() {
  const hasToken = !!getToken();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [soundOn, setSoundOn] = useState(() => {
    const stored = localStorage.getItem("admin_sound_on");
    return stored === null ? true : stored === "true";
  });
  const lastTotalRef = useRef(null);
  const isFirstLoadRef = useRef(true);

  // Audio beep generator (Web Audio API – no external file needed)
  const playBeep = () => {
    if (!soundOn) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.setValueAtTime(880, ctx.currentTime);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start();
      o.stop(ctx.currentTime + 0.4);
      // 2nd note
      setTimeout(() => {
        const o2 = ctx.createOscillator();
        const g2 = ctx.createGain();
        o2.connect(g2); g2.connect(ctx.destination);
        o2.type = "sine";
        o2.frequency.setValueAtTime(1320, ctx.currentTime);
        g2.gain.setValueAtTime(0.0001, ctx.currentTime);
        g2.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
        g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
        o2.start();
        o2.stop(ctx.currentTime + 0.4);
      }, 250);
    } catch {
      /* silent */
    }
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await fetchOrders({ status: status === "all" ? undefined : status, page, limit: 30 });
      setOrders(r.items);
      setTotal(r.total);
      setNewCount(r.new_count);
      setTotalPages(r.total_pages);

      // Detect new orders for live notification
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        lastTotalRef.current = r.total;
      } else if (lastTotalRef.current !== null && r.total > lastTotalRef.current) {
        const delta = r.total - lastTotalRef.current;
        playBeep();
        toast.success(`🛎️ ${delta} nouvelle${delta > 1 ? "s" : ""} commande${delta > 1 ? "s" : ""} reçue${delta > 1 ? "s" : ""} !`, {
          duration: 6000,
        });
        // Browser native notification (if user granted permission)
        if ("Notification" in window && Notification.permission === "granted") {
          try {
            new Notification("Braf_Zak — Nouvelle commande", {
              body: `${delta} commande${delta > 1 ? "s" : ""} en attente`,
              icon: "/logos/braf_zak_logo_main.jpeg",
            });
          } catch (notifErr) {
            if (process.env.NODE_ENV !== "production") console.error("Notification failed:", notifErr);
          }
        }
      }
      lastTotalRef.current = r.total;
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to load orders:", err);
      if (!silent) toast.error("Erreur de chargement");
    }
    if (!silent) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page]);

  useEffect(() => {
    if (hasToken) load();
  }, [hasToken, load]);

  // Poll every 15s for new orders
  useEffect(() => {
    if (!hasToken) return undefined;
    const interval = setInterval(() => load(true), 15000);
    return () => clearInterval(interval);
  }, [hasToken, load]);

  // Ask for browser notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch((err) => {
        if (process.env.NODE_ENV !== "production") console.error("Notification perm failed:", err);
      });
    }
  }, []);

  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      localStorage.setItem("admin_sound_on", String(next));
      toast.info(next ? "Son activé" : "Son désactivé");
      if (next) playBeep();
      return next;
    });
  };

  if (!hasToken) return <Navigate to="/admin/login" replace />;

  const changeStatus = async (id, next, order = null) => {
    try {
      await updateOrderStatus(id, next);
      toast.success("Statut mis à jour");
      // Auto-open WhatsApp with status message to the customer
      if (order && order.customer_phone) {
        const phone = order.customer_phone.replace(/\D/g, "");
        if (phone.length >= 8) {
          const name = order.customer_name || "";
          const total = formatPrice(order.total_gnf, "fr");
          const greeting = name ? `Bonjour ${name},` : "Bonjour,";
          let body = "";
          if (next === "contacted") {
            body = `${greeting}\n\nMerci d'avoir contacté brafzak.shop !\nNous vous contactons pour confirmer les détails de votre commande (${total}). ⚽✨`;
          } else if (next === "confirmed") {
            body = `${greeting}\n\n✅ Votre commande Braf_Zak (${total}) est CONFIRMÉE !\nNous la préparons et vous tenons informé(e) de la livraison. Merci pour votre confiance. ⚽👑`;
          } else if (next === "done") {
            body = `${greeting}\n\n🚚 Merci de nous avoir fait confiance.\nVotre commande (${total}) a été livrée depuis chez Braf_Zak. ⚽👑\n\nPartagez vos photos avec nous sur Instagram @braf_zak 🔥`;
          } else if (next === "cancelled") {
            body = `${greeting}\n\nNous sommes au regret de vous informer que votre commande Braf_Zak (${total}) a été annulée. N'hésitez pas à nous recontacter pour plus d'infos. 🙏`;
          }
          if (body) {
            const url = `https://wa.me/${phone}?text=${encodeURIComponent(body)}`;
            window.open(url, "_blank", "noopener,noreferrer");
          }
        }
      }
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const removeOrder = async (id) => {
    if (!window.confirm("Supprimer cette commande ?")) return;
    try {
      await deleteOrder(id);
      toast.success("Commande supprimée");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-orders-page">
      <header
        className="sticky top-0 z-30 border-b border-yellow-600/20 backdrop-blur-md"
        style={{ background: "rgba(5,5,5,0.85)" }}
      >
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/admin" className="flex items-center gap-3">
            <img src="/logos/braf_zak_logo_main.jpeg" alt="" className="w-10 h-10 rounded-full object-cover border border-yellow-500/40" />
            <div>
              <p className="text-[10px] tracking-[0.3em] text-yellow-400/70">ADMIN</p>
              <p className="text-lg font-black" style={{ fontFamily: '"Bebas Neue", system-ui', color: "#F4D77A", letterSpacing: "0.12em" }}>
                BRAF&nbsp;ZAK
              </p>
            </div>
          </Link>
          <nav className="flex items-center gap-2">
            <Link
              to="/admin"
              className="text-xs uppercase tracking-widest text-yellow-200/70 hover:text-yellow-300 px-3 py-2 rounded-full"
            >
              Catalogue
            </Link>
            <Link
              to="/admin/orders"
              className="relative text-xs uppercase tracking-widest text-black font-bold px-3 py-2 rounded-full"
              style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}
            >
              Commandes
              {newCount > 0 && (
                <span
                  data-testid="orders-new-badge"
                  className="absolute -top-1 -right-1 text-[9px] font-black w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ background: "#EF4444", color: "white" }}
                >
                  {newCount}
                </span>
              )}
            </Link>
            <Link
              to="/admin/lookbook"
              className="text-xs uppercase tracking-widest text-yellow-200/70 hover:text-yellow-300 px-3 py-2 rounded-full"
            >
              Lookbook
            </Link>
            <Link to="/" target="_blank" className="text-xs uppercase tracking-widest text-yellow-300 hover:text-yellow-200 px-3 py-2 rounded-full border border-yellow-500/30 inline-flex items-center gap-2">
              <ExternalLink className="w-3.5 h-3.5" /> Site
            </Link>
            <button
              onClick={toggleSound}
              data-testid="admin-sound-toggle"
              title={soundOn ? "Désactiver le son" : "Activer le son"}
              className={`text-xs uppercase tracking-widest px-3 py-2 rounded-full border inline-flex items-center gap-2 transition-colors ${
                soundOn
                  ? "border-yellow-400/60 text-yellow-200 hover:bg-yellow-500/10"
                  : "border-white/20 text-white/50 hover:text-white"
              }`}
            >
              {soundOn ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{soundOn ? "Son" : "Muet"}</span>
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="text-xs uppercase tracking-widest text-white/70 hover:text-red-300 px-3 py-2 rounded-full border border-white/20 inline-flex items-center gap-2">
              <LogOut className="w-3.5 h-3.5" /> Déco
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white" style={{ fontFamily: '"Bebas Neue", system-ui' }}>
              Commandes
            </h1>
            <p className="text-xs text-yellow-300/70 tracking-widest uppercase mt-1" data-testid="orders-total-count">
              {total} commande(s) {newCount > 0 && `· ${newCount} nouvelle(s)`}
            </p>
          </div>
        </div>

        {/* Status filter */}
        <div className="flex flex-wrap gap-2 mb-6 pb-5 border-b border-yellow-600/15">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => { setStatus(s.key); setPage(1); }}
              data-testid={`orders-filter-${s.key}`}
              className={`text-xs uppercase tracking-[0.2em] px-3.5 py-2 rounded-full transition-all ${
                status === s.key ? "text-black" : "text-yellow-200/80 border border-yellow-500/30 hover:border-yellow-400"
              }`}
              style={status === s.key ? { background: "linear-gradient(180deg, #F4D77A, #D4AF37)" } : undefined}
            >
              {s.label}
            </button>
          ))}
        </div>

        {(() => {
          if (loading) {
            return <p className="text-white/40 text-center py-12">···</p>;
          }
          if (orders.length === 0) {
            return (
              <div className="text-center py-20">
                <Package className="w-12 h-12 text-yellow-500/30 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-white/40">Aucune commande pour le moment.</p>
                <p className="text-white/30 text-xs mt-2">Les commandes apparaîtront ici dès qu'un client clique « Commander via WhatsApp ».</p>
              </div>
            );
          }
          return (
            <div className="space-y-3" data-testid="orders-list">
              {orders.map((o) => <OrderRow key={o.id} order={o} onStatus={changeStatus} onDelete={removeOrder} />)}
            </div>
          );
        })()}

        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-full border border-yellow-500/30 text-yellow-300 disabled:opacity-30 text-sm">
              ← Précédent
            </button>
            <span className="text-yellow-300 text-sm">{page} / {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-full border border-yellow-500/30 text-yellow-300 disabled:opacity-30 text-sm">
              Suivant →
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function OrderRow({ order, onStatus, onDelete }) {
  const [open, setOpen] = useState(false);
  const c = STATUS_COLORS[order.status] || STATUS_COLORS.new;
  const date = new Date(order.created_at);
  const dateStr = date.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  const first = order.items?.[0];
  const hasMore = (order.items?.length || 0) > 1;

  const buildWhatsAppLink = () => {
    const phone = order.customer_phone?.replace(/\D/g, "") || BRAND.whatsapp;
    const name = order.customer_name || "";
    const greeting = name ? `Bonjour ${name},` : "Bonjour,";
    const lines = order.items.map((it, i) => {
      const opts = [it.size, it.color].filter(Boolean).join(" / ");
      return `${i + 1}. ${it.name_fr}${opts ? ` (${opts})` : ""} × ${it.quantity} — ${formatPrice(it.price_gnf * it.quantity, "fr")}`;
    });
    const text = `${greeting}\n\nÀ propos de votre commande Braf_Zak :\n\n${lines.join("\n")}\n\nTotal : ${formatPrice(order.total_gnf, "fr")}`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  };

  return (
    <div
      data-testid={`order-row-${order.id}`}
      className="rounded-xl border border-yellow-500/15 bg-white/[0.02] hover:border-yellow-400/40 transition-colors overflow-hidden"
    >
      <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <div className="w-14 h-14 rounded-lg overflow-hidden bg-black flex items-center justify-center flex-shrink-0 border border-yellow-500/20">
          {first?.image ? (
            <img src={first.image} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-yellow-400/70 font-black text-sm" style={{ fontFamily: '"Bebas Neue", system-ui' }}>BZ</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-bold truncate">
              {first?.name_fr || "—"}
              {hasMore && <span className="text-yellow-300/60 text-xs ml-2">+{order.items.length - 1} autre(s)</span>}
            </h3>
            <span
              className="text-[10px] font-black tracking-[0.25em] px-2 py-0.5 rounded-full uppercase"
              style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}
              data-testid={`order-status-${order.id}`}
            >
              {STATUS_LABEL[order.status] || order.status}
            </span>
          </div>
          <p className="text-[11px] text-white/50 mt-1 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {dateStr}</span>
            {order.customer_name && (
              <span className="inline-flex items-center gap-1 text-yellow-200/80 font-medium">👤 {order.customer_name}</span>
            )}
            {order.customer_phone && (
              <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {order.customer_phone}</span>
            )}
            {order.source && <span className="opacity-60">· {order.source}</span>}
          </p>
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-yellow-300 font-black">{formatPrice(order.total_gnf, "fr")}</p>
          <p className="text-[10px] text-yellow-200/60">{order.items.length} article(s)</p>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-yellow-500/10" data-testid={`order-detail-${order.id}`}>
          <div className="grid sm:grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-300/70 mb-2">Articles</p>
              <ul className="space-y-2">
                {order.items.map((it, i) => (
                  <li key={`${it.slug}-${i}`} className="flex items-center gap-3 text-sm">
                    <div className="w-10 h-10 rounded bg-black flex items-center justify-center text-yellow-400/70 text-[10px] overflow-hidden border border-yellow-500/15 flex-shrink-0">
                      {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" /> : "BZ"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white truncate">{it.name_fr}</p>
                      <p className="text-[10px] text-white/50">
                        {[it.size, it.color].filter(Boolean).join(" · ")} {it.size || it.color ? "·" : ""} × {it.quantity}
                      </p>
                    </div>
                    <p className="text-yellow-300 text-xs font-bold flex-shrink-0">
                      {formatPrice(it.price_gnf * it.quantity, "fr")}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-300/70 mb-2">Total</p>
              <p className="text-2xl font-black text-yellow-200" style={{ fontFamily: '"Bebas Neue", system-ui' }}>
                {formatPrice(order.total_gnf, "fr")}
              </p>
              {order.customer_note && (
                <>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-300/70 mt-3 mb-1">Note client</p>
                  <p className="text-white/70 text-sm">{order.customer_note}</p>
                </>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <a
                  href={buildWhatsAppLink()}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`order-wa-${order.id}`}
                  className="text-xs px-3 py-2 rounded-full text-white inline-flex items-center gap-2"
                  style={{ background: "linear-gradient(180deg, #25D366, #128C7E)" }}
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Rappeler client
                </a>
                {["new", "contacted", "confirmed", "done", "cancelled"]
                  .filter((s) => s !== order.status)
                  .map((s) => (
                    <button
                      key={s}
                      onClick={(e) => { e.stopPropagation(); onStatus(order.id, s, order); }}
                      data-testid={`order-set-${s}-${order.id}`}
                      title={order.customer_phone ? `Met à jour le statut et envoie un WhatsApp à ${order.customer_name || "client"}` : "Met à jour le statut"}
                      className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-yellow-500/30 text-yellow-200/80 hover:border-yellow-400 hover:text-yellow-100 inline-flex items-center gap-1"
                    >
                      {STATUS_LABEL[s]}
                      {order.customer_phone && <MessageCircle className="w-3 h-3 opacity-70" />}
                    </button>
                  ))}
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(order.id); }}
                  data-testid={`order-delete-${order.id}`}
                  className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Suppr.
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
